import { useEffect, useState } from 'react'
import { supabase } from '../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'
const CLUB_NAME = 'Mighty Mufulira Wanderers'

const HEADERS = [
  { key: '#', tip: 'Position' },
  { key: 'Club', tip: 'Club Name' },
  { key: 'P', tip: 'Played' },
  { key: 'W', tip: 'Won' },
  { key: 'D', tip: 'Drawn' },
  { key: 'L', tip: 'Lost' },
  { key: 'GF', tip: 'Goals For' },
  { key: 'GA', tip: 'Goals Against' },
  { key: 'GD', tip: 'Goal Difference' },
  { key: 'Pts', tip: 'Points' },
]

function TooltipHeader({ label, tip }) {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  return (
    <th className="text-left py-3 px-2 sm:px-3 text-xs text-gray-500 uppercase tracking-widest font-semibold">
      <span
        className="cursor-help inline-block"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
      >
        {label}
      </span>

      {show && (
        <div
          className="fixed z-[9999] px-2 py-1 bg-gray-900 text-white text-xs font-semibold rounded whitespace-nowrap pointer-events-none"
          style={{
            left: pos.x,
            top: pos.y - 32,
            transform: 'translateX(-50%)',
          }}
        >
          {tip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </th>
  )
}

export default function LeagueTable() {
  const [table, setTable] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFixtures = async () => {
      const { data } = await supabase
        .from('fixtures').select('*').eq('club_id', CLUB_ID).eq('status', 'completed')

      if (!data) { setLoading(false); return }

      const standings = {}
      const addTeam = (team) => {
        if (!standings[team]) {
          standings[team] = { club: team, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 }
        }
      }

      data.forEach((fixture) => {
        const { home_team, away_team, home_score, away_score } = fixture
        addTeam(home_team)
        addTeam(away_team)
        standings[home_team].played++
        standings[away_team].played++
        standings[home_team].gf += home_score
        standings[home_team].ga += away_score
        standings[away_team].gf += away_score
        standings[away_team].ga += home_score
        if (home_score > away_score) {
          standings[home_team].won++
          standings[home_team].points += 3
          standings[away_team].lost++
        } else if (away_score > home_score) {
          standings[away_team].won++
          standings[away_team].points += 3
          standings[home_team].lost++
        } else {
          standings[home_team].drawn++
          standings[away_team].drawn++
          standings[home_team].points++
          standings[away_team].points++
        }
      })

      const sorted = Object.values(standings).sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points
        const gdA = a.gf - a.ga
        const gdB = b.gf - b.ga
        if (gdB !== gdA) return gdB - gdA
        return b.gf - a.gf
      })

      setTable(sorted)
      setLoading(false)
    }
    fetchFixtures()
  }, [])

  if (loading) return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6">
      <div className="text-gray-500 text-xs uppercase tracking-widest text-center py-8">Loading table...</div>
    </div>
  )

  if (table.length === 0) return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-6">
      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">League Table</div>
      <div className="text-gray-400 text-sm py-4">
        No completed fixtures yet — table will update after matches are played.
      </div>
    </div>
  )

  return (
    <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-4 sm:p-6">
      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4 sm:mb-6">
        League Table
      </div>
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="min-w-[500px] px-4 sm:px-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-white/10">
                {HEADERS.map((h) => (
                  <TooltipHeader key={h.key} label={h.key} tip={h.tip} />
                ))}
              </tr>
            </thead>
            <tbody>
              {table.map((row, index) => {
                const gd = row.gf - row.ga
                const isOurClub = row.club === CLUB_NAME
                return (
                  <tr
                    key={row.club}
                    className={`border-b border-gray-200 dark:border-white/5 transition-colors ${
                      isOurClub ? 'bg-green-50 dark:bg-green-900/20' : 'hover:bg-gray-50 dark:hover:bg-white/5'
                    }`}
                  >
                    <td className={`py-3 px-2 sm:px-3 font-bold ${isOurClub ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`}>
                      {index + 1}
                    </td>
                    <td className={`py-3 px-2 sm:px-3 font-semibold text-xs sm:text-sm ${isOurClub ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                      {row.club}
                    </td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.played}</td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.won}</td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.drawn}</td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.lost}</td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.gf}</td>
                    <td className="py-3 px-2 sm:px-3 text-gray-500">{row.ga}</td>
                    <td className={`py-3 px-2 sm:px-3 font-semibold ${gd > 0 ? 'text-green-600 dark:text-green-400' : gd < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                      {gd > 0 ? `+${gd}` : gd}
                    </td>
                    <td className={`py-3 px-2 sm:px-3 font-black ${isOurClub ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                      {row.points}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}